import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { AppLayout, routeFromHash, type RouteId } from './components/Layout';
import { clearAuthTokens, getCurrentUser, isLocalDevHost, loadAdmin, loadCaptures, loadChat, loadCr, loadFunnel, loadInsights, loadOnline, loadOpd, loadOpdRaw, loadOpsComplaints, loadOpsQuality, loadOpsReviews, loadOpsSummary, loadOpsTcdt, loadOpsWarranty, loadOverview, loadProductDemand, loadSecondhand, loadSecondhandSales, loadStaff, loadStatus, localDevToken, loginWithGAuth, logoutFromGAuth } from './api';
import { AuthShell, LoginScreen } from './screens/LoginScreen';
import { OverviewScreen } from './screens/OverviewScreen';
import { AdminScreen } from './screens/AdminScreen';
import { FunnelScreen } from './screens/FunnelScreen';
import { DetailScreen } from './screens/DetailScreen';
import { ChatScreen } from './screens/ChatScreen';
import { OnlineScreen } from './screens/OnlineScreen';
import { InsightsScreen } from './screens/InsightsScreen';
import { SecondhandScreen } from './screens/SecondhandScreen';
import { ProductDemandScreen } from './screens/ProductDemandScreen';
import { StatusScreen } from './screens/StatusScreen';
import { StaffScreen } from './screens/StaffScreen';
import { CrScreen } from './screens/CrScreen';
import { OpdScreen } from './screens/OpdScreen';
import { OpdRawScreen } from './screens/OpdRawScreen';
import { OpsQualityScreen } from './screens/OpsQualityScreen';
import { OpsComplaintsScreen } from './screens/OpsComplaintsScreen';
import { OpsWarrantyScreen } from './screens/OpsWarrantyScreen';
import { OpsReviewsScreen } from './screens/OpsReviewsScreen';
import { OpsTcdtScreen } from './screens/OpsTcdtScreen';
import type { AdminResponse, AuthUser, CaptureStaffResponse, ChatResponse, CrResponse, FunnelResponse, InsightsResponse, NormalizedDetailRow, OnlineResponse, OpdRawResponse, OpdResponse, OpsComplaintsResponse, OpsQualityResponse, OpsReviewsResponse, OpsSummaryResponse, OpsTcdtResponse, OpsWarrantyResponse, OverviewResponse, ProductDemandResponse, SecondhandResponse, SecondhandSalesResponse, StatusResponse } from './types';
import { normalizeDetailRow } from './utils';
import { DEFAULT_RANGE } from './ranges';
import './styles.css';

const emptyDetailFilters = { q: '', store: '', staff: '', status: '', need: '', industry: '', brand: '', month: '', contact: '', qc: '' };
const emptyChatFilters = { store: '', contact: '', status: '', linked: '' };
const emptyOnlineFilters = { channel: '', intent: '', priority: '' };

function App() {
  const [screen, setScreen] = useState<RouteId>(routeFromHash);
  const [overview, setOverview] = useState<OverviewResponse | null>(null);
  const [detailRows, setDetailRows] = useState<NormalizedDetailRow[]>([]);
  const [detailLoaded, setDetailLoaded] = useState(false);
  const [detailBusy, setDetailBusy] = useState(false);
  const [chatData, setChatData] = useState<ChatResponse | null>(null);
  const [chatLoaded, setChatLoaded] = useState(false);
  const [chatBusy, setChatBusy] = useState(false);
  const [funnelData, setFunnelData] = useState<FunnelResponse | null>(null);
  const [funnelLoaded, setFunnelLoaded] = useState(false);
  const [funnelBusy, setFunnelBusy] = useState(false);
  const [insightsData, setInsightsData] = useState<InsightsResponse | null>(null);
  const [insightsLoaded, setInsightsLoaded] = useState(false);
  const [insightsBusy, setInsightsBusy] = useState(false);
  const [insightsRange, setInsightsRange] = useState(DEFAULT_RANGE);
  const [secondhandData, setSecondhandData] = useState<SecondhandResponse | null>(null);
  const [secondhandLoaded, setSecondhandLoaded] = useState(false);
  const [secondhandBusy, setSecondhandBusy] = useState(false);
  const [secondhandRange, setSecondhandRange] = useState(DEFAULT_RANGE);
  const [secondhandSales, setSecondhandSales] = useState<SecondhandSalesResponse | null>(null);
  const [secondhandSalesBusy, setSecondhandSalesBusy] = useState(false);
  const [secondhandSalesError, setSecondhandSalesError] = useState('');
  const [productData, setProductData] = useState<ProductDemandResponse | null>(null);
  const [productLoaded, setProductLoaded] = useState(false);
  const [productBusy, setProductBusy] = useState(false);
  const [statusData, setStatusData] = useState<StatusResponse | null>(null);
  const [statusLoaded, setStatusLoaded] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [overviewBusy, setOverviewBusy] = useState(false);
  const [apiError, setApiError] = useState('');
  const [authChecking, setAuthChecking] = useState(true);
  const [authUser, setAuthUser] = useState<AuthUser | null>(null);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [detailFilters, setDetailFilters] = useState(emptyDetailFilters);
  const [chatFilters, setChatFilters] = useState(emptyChatFilters);
  const [staffData, setStaffData] = useState<CaptureStaffResponse | null>(null);
  const [staffLoaded, setStaffLoaded] = useState(false);
  const [staffBusy, setStaffBusy] = useState(false);
  const [staffStore, setStaffStore] = useState('');
  const [staffRange, setStaffRange] = useState(DEFAULT_RANGE);
  const [crData, setCrData] = useState<CrResponse | null>(null);
  const [crLoaded, setCrLoaded] = useState(false);
  const [crBusy, setCrBusy] = useState(false);
  const [crRange, setCrRange] = useState(DEFAULT_RANGE);
  const [adminData, setAdminData] = useState<AdminResponse | null>(null);
  const [adminLoaded, setAdminLoaded] = useState(false);
  const [adminBusy, setAdminBusy] = useState(false);
  const [adminRange, setAdminRange] = useState(DEFAULT_RANGE);
  const [onlineData, setOnlineData] = useState<OnlineResponse | null>(null);
  const [onlineLoaded, setOnlineLoaded] = useState(false);
  const [onlineBusy, setOnlineBusy] = useState(false);
  const [onlineRange, setOnlineRange] = useState(DEFAULT_RANGE);
  const [onlineServerFilters, setOnlineServerFilters] = useState<Record<string, string[]>>({});
  const [onlineFilters, setOnlineFilters] = useState(emptyOnlineFilters);
  const [opdData, setOpdData] = useState<OpdResponse | null>(null);
  const [opdLoaded, setOpdLoaded] = useState(false);
  const [opdBusy, setOpdBusy] = useState(false);
  const [opdRange, setOpdRange] = useState('30d');
  const [opdRawData, setOpdRawData] = useState<OpdRawResponse | null>(null);
  const [opdRawLoaded, setOpdRawLoaded] = useState(false);
  const [opdRawBusy, setOpdRawBusy] = useState(false);
  const [opdRawRange, setOpdRawRange] = useState('30d');
  // Nhóm "Chất lượng vận hành" (nguồn ops-quality D1): 5 màn hình chọn theo tháng.
  const [opsMonth, setOpsMonth] = useState('');
  // Tóm tắt cho trang Tổng quan — tải song song, lỗi không chặn overview.
  const [opsSummary, setOpsSummary] = useState<OpsSummaryResponse | null>(null);
  const [opsSummaryLoaded, setOpsSummaryLoaded] = useState(false);
  const [opsSummaryBusy, setOpsSummaryBusy] = useState(false);
  const [opsQualityData, setOpsQualityData] = useState<OpsQualityResponse | null>(null);
  const [opsQualityLoaded, setOpsQualityLoaded] = useState(false);
  const [opsQualityBusy, setOpsQualityBusy] = useState(false);
  const [opsComplaintsData, setOpsComplaintsData] = useState<OpsComplaintsResponse | null>(null);
  const [opsComplaintsLoaded, setOpsComplaintsLoaded] = useState(false);
  const [opsComplaintsBusy, setOpsComplaintsBusy] = useState(false);
  const [opsWarrantyData, setOpsWarrantyData] = useState<OpsWarrantyResponse | null>(null);
  const [opsWarrantyLoaded, setOpsWarrantyLoaded] = useState(false);
  const [opsWarrantyBusy, setOpsWarrantyBusy] = useState(false);
  const [opsReviewsData, setOpsReviewsData] = useState<OpsReviewsResponse | null>(null);
  const [opsReviewsLoaded, setOpsReviewsLoaded] = useState(false);
  const [opsReviewsBusy, setOpsReviewsBusy] = useState(false);
  const [opsTcdtData, setOpsTcdtData] = useState<OpsTcdtResponse | null>(null);
  const [opsTcdtLoaded, setOpsTcdtLoaded] = useState(false);
  const [opsTcdtBusy, setOpsTcdtBusy] = useState(false);

  const tokenOrLocal = () => localDevToken();

  const loadOverviewData = async (tokenOverride = '') => {
    setOverviewBusy(true);
    setApiError('');
    try {
      setOverview(await loadOverview(tokenOverride || tokenOrLocal()));
    } catch (error) {
      const message = String((error as Error).message || error);
      const status = (error as { status?: number }).status;
      if (status === 401 || message.includes('unauthorized')) {
        clearAuthTokens();
        setAuthUser(null);
      }
      setApiError(message);
    } finally {
      setOverviewBusy(false);
    }
  };

  const loadDetailData = async (force = false) => {
    if (detailBusy || (detailLoaded && !force)) return;
    setDetailBusy(true);
    setApiError('');
    try {
      const payload = await loadCaptures(tokenOrLocal());
      setDetailRows((payload.details || []).map(normalizeDetailRow));
      setDetailLoaded(true);
    } catch (error) {
      setApiError(String((error as Error).message || error));
    } finally {
      setDetailBusy(false);
    }
  };

  const loadChatData = async (force = false) => {
    if (chatBusy || (chatLoaded && !force)) return;
    setChatBusy(true);
    setApiError('');
    try {
      setChatData(await loadChat(tokenOrLocal()));
      setChatLoaded(true);
    } catch (error) {
      setApiError(String((error as Error).message || error));
    } finally {
      setChatBusy(false);
    }
  };

  const loadFunnelData = async (force = false) => {
    if (funnelBusy || (funnelLoaded && !force)) return;
    setFunnelBusy(true);
    setApiError('');
    try {
      setFunnelData(await loadFunnel(tokenOrLocal()));
      setFunnelLoaded(true);
    } catch (error) {
      setApiError(String((error as Error).message || error));
    } finally {
      setFunnelBusy(false);
    }
  };

  const loadInsightsData = async (force = false, rangeOverride?: string) => {
    if (insightsBusy || (insightsLoaded && !force)) return;
    setInsightsBusy(true);
    setApiError('');
    try {
      setInsightsData(await loadInsights(tokenOrLocal(), rangeOverride || insightsRange));
      setInsightsLoaded(true);
    } catch (error) {
      setApiError(String((error as Error).message || error));
    } finally {
      setInsightsBusy(false);
    }
  };
  const changeInsightsRange = (range: string) => {
    setInsightsRange(range);
    void loadInsightsData(true, range);
  };

  const loadSecondhandData = async (force = false, rangeOverride?: string) => {
    if (secondhandBusy || (secondhandLoaded && !force)) return;
    setSecondhandBusy(true);
    setApiError('');
    // Sales (DWH) loads on its own track: a slow or failing f_sales query never blocks the chat section.
    setSecondhandSalesBusy(true);
    setSecondhandSalesError('');
    loadSecondhandSales(tokenOrLocal(), rangeOverride || secondhandRange)
      .then(setSecondhandSales)
      .catch(error => setSecondhandSalesError(String((error as Error).message || error)))
      .finally(() => setSecondhandSalesBusy(false));
    try {
      setSecondhandData(await loadSecondhand(tokenOrLocal(), rangeOverride || secondhandRange));
      setSecondhandLoaded(true);
    } catch (error) {
      setApiError(String((error as Error).message || error));
    } finally {
      setSecondhandBusy(false);
    }
  };
  const changeSecondhandRange = (range: string) => {
    setSecondhandRange(range);
    void loadSecondhandData(true, range);
  };

  const loadProductData = async (force = false) => {
    if (productBusy || (productLoaded && !force)) return;
    setProductBusy(true);
    setApiError('');
    try {
      setProductData(await loadProductDemand(tokenOrLocal()));
      setProductLoaded(true);
    } catch (error) {
      setApiError(String((error as Error).message || error));
    } finally {
      setProductBusy(false);
    }
  };

  const loadStatusData = async (force = false) => {
    if (statusBusy || (statusLoaded && !force)) return;
    setStatusBusy(true);
    setApiError('');
    try {
      setStatusData(await loadStatus(tokenOrLocal()));
      setStatusLoaded(true);
    } catch (error) {
      setApiError(String((error as Error).message || error));
    } finally {
      setStatusBusy(false);
    }
  };

  const loadStaffData = async (force = false, rangeOverride?: string) => {
    if (staffBusy || (staffLoaded && !force)) return;
    setStaffBusy(true);
    setApiError('');
    try {
      setStaffData(await loadStaff(tokenOrLocal(), rangeOverride || staffRange));
      setStaffLoaded(true);
    } catch (error) {
      setApiError(String((error as Error).message || error));
    } finally {
      setStaffBusy(false);
    }
  };
  const changeStaffRange = (r: string) => { setStaffRange(r); void loadStaffData(true, r); };
  const loadCrData = async (force = false, rangeOverride?: string) => {
    if (crBusy || (crLoaded && !force)) return;
    setCrBusy(true);
    setApiError('');
    try {
      setCrData(await loadCr(rangeOverride || crRange, tokenOrLocal()));
      setCrLoaded(true);
    } catch (error) {
      setApiError(String((error as Error).message || error));
    } finally {
      setCrBusy(false);
    }
  };
  const changeCrRange = (r: string) => { setCrRange(r); void loadCrData(true, r); };

  const loadAdminData = async (force = false, rangeOverride?: string) => {
    if (adminBusy || (adminLoaded && !force)) return;
    setAdminBusy(true);
    setApiError('');
    try {
      setAdminData(await loadAdmin(tokenOrLocal(), rangeOverride || adminRange));
      setAdminLoaded(true);
    } catch (error) {
      setApiError(String((error as Error).message || error));
    } finally {
      setAdminBusy(false);
    }
  };
  const changeAdminRange = (r: string) => { setAdminRange(r); void loadAdminData(true, r); };

  const loadOnlineData = async (force = false, rangeOverride?: string, filtersOverride?: Record<string, string[]>) => {
    if (onlineBusy || (onlineLoaded && !force)) return;
    setOnlineBusy(true);
    setApiError('');
    try {
      setOnlineData(await loadOnline(tokenOrLocal(), rangeOverride || onlineRange, filtersOverride || onlineServerFilters));
      setOnlineLoaded(true);
    } catch (error) {
      setApiError(String((error as Error).message || error));
    } finally {
      setOnlineBusy(false);
    }
  };
  const changeOnlineRange = (r: string) => {
    setOnlineRange(r);
    setOnlineServerFilters({});
    void loadOnlineData(true, r, {});
  };
  const changeOnlineFilters = (filters: Record<string, string[]>) => {
    setOnlineServerFilters(filters);
    void loadOnlineData(true, onlineRange, filters);
  };

  const loadOpdData = async (force = false, rangeOverride?: string) => {
    if (opdBusy || (opdLoaded && !force)) return;
    setOpdBusy(true);
    setApiError('');
    try {
      setOpdData(await loadOpd(tokenOrLocal(), rangeOverride || opdRange));
      setOpdLoaded(true);
    } catch (error) {
      setApiError(String((error as Error).message || error));
    } finally {
      setOpdBusy(false);
    }
  };
  const changeOpdRange = (range: string) => { setOpdRange(range); void loadOpdData(true, range); };
  const loadOpdRawData = async (force = false, rangeOverride?: string) => {
    if (opdRawBusy || (opdRawLoaded && !force)) return;
    setOpdRawBusy(true);
    try {
      setOpdRawData(await loadOpdRaw(tokenOrLocal(), rangeOverride || opdRawRange));
      setOpdRawLoaded(true);
    } catch (error) {
      setOpdRawData({ error: String((error as Error)?.message || error) });
      setOpdRawLoaded(true);
    } finally {
      setOpdRawBusy(false);
    }
  };
  const changeOpdRawRange = (range: string) => { setOpdRawRange(range); void loadOpdRawData(true, range); };

  // Tóm tắt Chất lượng vận hành cho Tổng quan — best-effort: lỗi ghi vào payload,
  // KHÔNG set apiError để không che dashboard chính.
  const loadOpsSummaryData = async (force = false) => {
    if (opsSummaryBusy || (opsSummaryLoaded && !force)) return;
    setOpsSummaryBusy(true);
    try {
      setOpsSummary(await loadOpsSummary(tokenOrLocal()));
    } catch (error) {
      setOpsSummary({ error: String((error as Error).message || error) });
    } finally {
      setOpsSummaryLoaded(true);
      setOpsSummaryBusy(false);
    }
  };

  // ═══════ Nhóm "Chất lượng vận hành" — loader chung tham số tháng ═══════
  const loadOpsQualityData = async (force = false, monthOverride?: string) => {
    if (opsQualityBusy || (opsQualityLoaded && !force)) return;
    setOpsQualityBusy(true);
    setApiError('');
    try {
      setOpsQualityData(await loadOpsQuality(tokenOrLocal(), monthOverride ?? opsMonth));
      setOpsQualityLoaded(true);
    } catch (error) {
      setApiError(String((error as Error).message || error));
    } finally {
      setOpsQualityBusy(false);
    }
  };

  const loadOpsComplaintsData = async (force = false, monthOverride?: string) => {
    if (opsComplaintsBusy || (opsComplaintsLoaded && !force)) return;
    setOpsComplaintsBusy(true);
    setApiError('');
    try {
      setOpsComplaintsData(await loadOpsComplaints(tokenOrLocal(), monthOverride ?? opsMonth));
      setOpsComplaintsLoaded(true);
    } catch (error) {
      setApiError(String((error as Error).message || error));
    } finally {
      setOpsComplaintsBusy(false);
    }
  };

  const loadOpsWarrantyData = async (force = false, monthOverride?: string) => {
    if (opsWarrantyBusy || (opsWarrantyLoaded && !force)) return;
    setOpsWarrantyBusy(true);
    setApiError('');
    try {
      setOpsWarrantyData(await loadOpsWarranty(tokenOrLocal(), monthOverride ?? opsMonth));
      setOpsWarrantyLoaded(true);
    } catch (error) {
      setApiError(String((error as Error).message || error));
    } finally {
      setOpsWarrantyBusy(false);
    }
  };

  const loadOpsReviewsData = async (force = false, monthOverride?: string) => {
    if (opsReviewsBusy || (opsReviewsLoaded && !force)) return;
    setOpsReviewsBusy(true);
    setApiError('');
    try {
      setOpsReviewsData(await loadOpsReviews(tokenOrLocal(), monthOverride ?? opsMonth));
      setOpsReviewsLoaded(true);
    } catch (error) {
      setApiError(String((error as Error).message || error));
    } finally {
      setOpsReviewsBusy(false);
    }
  };

  const loadOpsTcdtData = async (force = false, monthOverride?: string) => {
    if (opsTcdtBusy || (opsTcdtLoaded && !force)) return;
    setOpsTcdtBusy(true);
    setApiError('');
    try {
      setOpsTcdtData(await loadOpsTcdt(tokenOrLocal(), monthOverride ?? opsMonth));
      setOpsTcdtLoaded(true);
    } catch (error) {
      setApiError(String((error as Error).message || error));
    } finally {
      setOpsTcdtBusy(false);
    }
  };

  // Đổi tháng: chỉ reload màn hình ops đang mở; các màn ops khác reset để lần
  // mở kế tiếp tự tải theo tháng mới.
  const changeOpsMonth = (month: string) => {
    setOpsMonth(month);
    setOpsQualityLoaded(false);
    setOpsComplaintsLoaded(false);
    setOpsWarrantyLoaded(false);
    setOpsReviewsLoaded(false);
    setOpsTcdtLoaded(false);
    if (screen === 'quality') void loadOpsQualityData(true, month);
    if (screen === 'complaints') void loadOpsComplaintsData(true, month);
    if (screen === 'warranty') void loadOpsWarrantyData(true, month);
    if (screen === 'reviews') void loadOpsReviewsData(true, month);
    if (screen === 'tcdt') void loadOpsTcdtData(true, month);
  };

  const goTo = (route: RouteId) => {
    if (window.location.hash !== `#/${route}`) window.location.hash = `/${route}`;
    setScreen(route);
  };

  const submitLogin = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setLoginLoading(true);
    setLoginError('');
    setApiError('');
    try {
      const auth = await loginWithGAuth(String(form.get('username') || '').trim(), String(form.get('password') || ''));
      setAuthUser(auth.user);
      await loadOverviewData(auth.token);
    } catch (error) {
      setLoginError(String((error as Error).message || error));
    } finally {
      setLoginLoading(false);
    }
  };

  const logout = async () => {
    await logoutFromGAuth();
    setAuthUser(null);
    setOverview(null);
    setOpsSummary(null);
    setOpsSummaryLoaded(false);
    setDetailRows([]);
    setDetailLoaded(false);
    setChatData(null);
    setChatLoaded(false);
    setFunnelData(null);
    setFunnelLoaded(false);
    setInsightsData(null);
    setInsightsLoaded(false);
    setSecondhandData(null);
    setSecondhandLoaded(false);
    setSecondhandSales(null);
    setProductData(null);
    setProductLoaded(false);
    setStatusData(null);
    setStatusLoaded(false);
    setStaffData(null);
    setStaffLoaded(false);
    setCrData(null);
    setCrLoaded(false);
    setAdminData(null);
    setAdminLoaded(false);
    setOnlineData(null);
    setOnlineLoaded(false);
    setOpdData(null);
    setOpdLoaded(false);
    setOpdRawData(null);
    setOpdRawLoaded(false);
    setOpsMonth('');
    setOpsQualityData(null);
    setOpsQualityLoaded(false);
    setOpsComplaintsData(null);
    setOpsComplaintsLoaded(false);
    setOpsWarrantyData(null);
    setOpsWarrantyLoaded(false);
    setOpsReviewsData(null);
    setOpsReviewsLoaded(false);
    setOpsTcdtData(null);
    setOpsTcdtLoaded(false);
    setScreen('overview');
    window.location.hash = '/overview';
    setApiError('');
  };

  const reload = () => {
    void loadOverviewData();
    if (opsSummaryLoaded) void loadOpsSummaryData(true);
    if (detailLoaded) void loadDetailData(true);
    if (chatLoaded) void loadChatData(true);
    if (funnelLoaded) void loadFunnelData(true);
    if (insightsLoaded) void loadInsightsData(true);
    if (secondhandLoaded) void loadSecondhandData(true);
    if (productLoaded) void loadProductData(true);
    if (statusLoaded) void loadStatusData(true);
    if (staffLoaded) void loadStaffData(true);
    if (crLoaded) void loadCrData(true);
    if (adminLoaded) void loadAdminData(true);
    if (onlineLoaded) void loadOnlineData(true);
    if (opdLoaded) void loadOpdData(true);
    if (opdRawLoaded) void loadOpdRawData(true);
    if (opsQualityLoaded) void loadOpsQualityData(true);
    if (opsComplaintsLoaded) void loadOpsComplaintsData(true);
    if (opsWarrantyLoaded) void loadOpsWarrantyData(true);
    if (opsReviewsLoaded) void loadOpsReviewsData(true);
    if (opsTcdtLoaded) void loadOpsTcdtData(true);
  };

  useEffect(() => {
    const boot = async () => {
      clearAuthTokens();
      if (isLocalDevHost()) {
        setAuthUser({ name: 'Local dev' });
        setAuthChecking(false);
        void loadOverviewData(localDevToken());
        return;
      }
      try {
        setAuthUser(await getCurrentUser());
        setAuthChecking(false);
        void loadOverviewData();
      } catch (error) {
        const status = (error as { status?: number }).status;
        if (status === 401) {
          setAuthChecking(false);
          return;
        }
        // Transient verify failure (504 upstream timeout / network stall): the HttpOnly cookie
        // was not rejected, so keep a degraded shell and let protected APIs self-correct.
        setAuthUser({ name: 'G-Auth' });
        setApiError(String((error as Error).message || error));
        setAuthChecking(false);
        void loadOverviewData();
      }
    };
    void boot();
  }, []);

  useEffect(() => {
    const onRoute = () => setScreen(routeFromHash());
    window.addEventListener('hashchange', onRoute);
    return () => window.removeEventListener('hashchange', onRoute);
  }, []);

  useEffect(() => {
    if (!authUser) return;
    if (screen === 'overview') void loadOpsSummaryData();
    if (screen === 'detail') void loadDetailData();
    if (screen === 'chat') void loadChatData();
    if (screen === 'funnel') void loadFunnelData();
    if (screen === 'insights') void loadInsightsData();
    if (screen === 'secondhand') void loadSecondhandData();
    if (screen === 'products') void loadProductData();
    if (screen === 'status') void loadStatusData();
    if (screen === 'staff') void loadStaffData();
    if (screen === 'cr') void loadCrData();
    if (screen === 'admin') void loadAdminData();
    if (screen === 'online') void loadOnlineData();
    if (screen === 'opd') void loadOpdData();
    if (screen === 'opdraw') void loadOpdRawData();
    if (screen === 'quality') void loadOpsQualityData();
    if (screen === 'complaints') void loadOpsComplaintsData();
    if (screen === 'warranty') void loadOpsWarrantyData();
    if (screen === 'reviews') void loadOpsReviewsData();
    if (screen === 'tcdt') void loadOpsTcdtData();
  }, [screen, authUser]);

  const storeList = useMemo(() => (overview?.funnel || []).map(row => row.store_code), [overview]);
  const isBusy = overviewBusy || detailBusy || chatBusy || funnelBusy || insightsBusy || secondhandBusy || statusBusy || staffBusy || adminBusy || onlineBusy || opdBusy || opdRawBusy || opsQualityBusy || opsComplaintsBusy || opsWarrantyBusy || opsReviewsBusy || opsTcdtBusy;

  if (authChecking) return <AuthShell title="Đang kiểm tra đăng nhập" subtitle="Đang xác thực tài khoản..." />;
  if (!authUser) return <LoginScreen error={loginError || apiError} loading={loginLoading} onSubmit={submitLogin} />;

  return (
    <AppLayout
      screen={screen}
      authUser={authUser}
      apiSource={overview?.meta?.source || 'AI Hub MCP'}
      refreshedAt={overview?.meta?.refreshed_at}
      isBusy={isBusy}
      apiError={apiError}
      onRoute={goTo}
      onReload={reload}
      onLogout={logout}
    >
      {screen === 'overview' && <OverviewScreen data={overview} loading={overviewBusy} opsSummary={opsSummary} onPickStore={(store) => { setDetailFilters(current => ({ ...current, store })); goTo('detail'); }} />}
      {screen === 'admin' && <AdminScreen data={adminData} loaded={adminLoaded} busy={adminBusy} range={adminRange} onRange={changeAdminRange} />}
      {screen === 'opd' && <OpdScreen data={opdData} loaded={opdLoaded} busy={opdBusy} range={opdRange} onRange={changeOpdRange} />}
      {screen === 'opdraw' && <OpdRawScreen data={opdRawData} loaded={opdRawLoaded} busy={opdRawBusy} range={opdRawRange} onRange={changeOpdRawRange} />}
      {screen === 'insights' && <InsightsScreen data={insightsData} loaded={insightsLoaded} busy={insightsBusy} range={insightsRange} onRange={changeInsightsRange} />}
      {screen === 'secondhand' && <SecondhandScreen data={secondhandData} loaded={secondhandLoaded} busy={secondhandBusy} range={secondhandRange} onRange={changeSecondhandRange} sales={secondhandSales} salesBusy={secondhandSalesBusy} salesError={secondhandSalesError} />}
      {screen === 'products' && <ProductDemandScreen data={productData} loaded={productLoaded} busy={productBusy} />}
      {screen === 'status' && <StatusScreen data={statusData} loaded={statusLoaded} busy={statusBusy} onReload={() => loadStatusData(true)} />}
      {screen === 'funnel' && <FunnelScreen data={funnelData} loaded={funnelLoaded} busy={funnelBusy} />}
      {screen === 'detail' && <DetailScreen rows={detailRows} loaded={detailLoaded} busy={detailBusy} filters={detailFilters} setFilters={setDetailFilters} storeList={storeList} />}
      {screen === 'chat' && <ChatScreen data={chatData} loaded={chatLoaded} busy={chatBusy} filters={chatFilters} setFilters={setChatFilters} />}
      {screen === 'online' && <OnlineScreen data={onlineData} loaded={onlineLoaded} busy={onlineBusy} filters={onlineFilters} setFilters={setOnlineFilters} range={onlineRange} onRange={changeOnlineRange} serverFilters={onlineServerFilters} onServerFilters={changeOnlineFilters} />}
      {screen === 'staff' && <StaffScreen data={staffData} loaded={staffLoaded} busy={staffBusy} store={staffStore} setStore={setStaffStore} range={staffRange} onRange={changeStaffRange} />}
      {screen === 'cr' && <CrScreen key={crData?.meta.range || 'cr'} data={crData} loaded={crLoaded} busy={crBusy} range={crRange} onRange={changeCrRange} />}
      {screen === 'quality' && <OpsQualityScreen data={opsQualityData} loaded={opsQualityLoaded} busy={opsQualityBusy} month={opsMonth || (opsQualityData?.meta?.month ?? '')} onMonth={changeOpsMonth} />}
      {screen === 'complaints' && <OpsComplaintsScreen data={opsComplaintsData} loaded={opsComplaintsLoaded} busy={opsComplaintsBusy} month={opsMonth || (opsComplaintsData?.meta?.month ?? '')} onMonth={changeOpsMonth} />}
      {screen === 'warranty' && <OpsWarrantyScreen data={opsWarrantyData} loaded={opsWarrantyLoaded} busy={opsWarrantyBusy} month={opsMonth || (opsWarrantyData?.meta?.month ?? '')} onMonth={changeOpsMonth} />}
      {screen === 'reviews' && <OpsReviewsScreen data={opsReviewsData} loaded={opsReviewsLoaded} busy={opsReviewsBusy} month={opsMonth || (opsReviewsData?.meta?.month ?? '')} onMonth={changeOpsMonth} />}
      {screen === 'tcdt' && <OpsTcdtScreen data={opsTcdtData} loaded={opsTcdtLoaded} busy={opsTcdtBusy} month={opsMonth || (opsTcdtData?.meta?.month ?? '')} onMonth={changeOpsMonth} />}
    </AppLayout>
  );
}

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
